import React from 'react';
import { Prompt, Redirect } from 'react-router-dom';
import ModalEnabled from '../extend/ModalEnabled';
import DetailsBar from '../shared/DetailsBar';
import BackButton from '../shared/BackButton';
import HeadingPlaceholder from '../shared/HeadingPlaceholder';
import ContactInformationBar from '../shared/ContactInformationBar';
import AccountStatus from '../shared/AccountStatus';
import TextAreaView from '../shared/TextAreaView';
import TextAreaModal from '../shared/TextAreaModal';
import { chefAPI } from '../../InterfaceAPI/chefAPI';
import ChefEditDetailsModal from '../chef/ChefEditDetailsModal';
import ChefAvailabilitySection from '../chef/ChefAvailabilitySection';
import {
  maskCapitalizeFirstLetter,
  maskMoney,
  maskServiceEndTime,
  maskServiceStartTime,
  maskDate,
  adminChefName
} from '../../masks';
import withSplashMessage from '../splashMessage/withSplashMessage';
import ChefServices from '../chef/ChefServices';
import ChefAccountStatusModal from '../chef/ChefAccountStatusModal';
import ChefTimeOffModal from '../chef/ChefTimeOffModal';
import { stringfyServerErrors } from '../../helpers/util';
import SVGTrash from '../../svg/Trash';
import ConfirmModal from '../shared/ConfirmModal';
import ChefLocationModal from '../chef/ChefLocationModal';
import ChefLocation from '../chef/ChefLocation';
import { authAPI } from '../../InterfaceAPI/authAPI';
import ENV_CONFIG from '../../env';
import Loader from '../shared/Loader';
import {
  createZipMap,
  maskServiceDistricts
} from '../../helpers/serviceDistrictHelpers.js';
import { get as _get } from 'lodash'
import { ACCOUNT_STATUSES } from '../../const.js';

class AdminChef extends ModalEnabled {
  defaultChefState = {
    personal_addresses: [],
    vip: 0
  };
  state = {
    chef: {
      ...this.defaultChefState
    },
    availableServiceDistricts: [],
    timeOffIndex: -1,
    isLoading: false,
    unsaved: false,
    redirect_to: null,
    refresh: false,
    zipMap: {},
    newStatus: null,
  };

  detailsBarProperties = [
    {
      label: 'Service Count',
      combine: [
        { key: 'services_stats', dive: 'total_weekly', mask: (val) => `${val}`  },
        { raw: ' Weekly' },
        { raw: ' ', className: '--new-line' },
        { key: 'services_stats', dive: 'total_occasion', mask: (val) => `${val}`  },
        { raw: ' Occasion' }
      ]
    },
    {
      label: 'Available For',
      key: 'occasion_opt_in',
      mask: this.maskAvailability
    },
    {
      label: 'Locations',
      key: 'service_districts',
      mask: maskServiceDistricts
    },
    {
      label: 'Total Services This Month',
      combine: [
        { dive: 'this_month', key: 'services_stats' },
        {
          dive: 'this_month_completed',
          key: 'services_stats',
          mask: this.maskCompleted
        }
      ]
    },
    { label: 'Total Services Completed', dive: 'total', key: 'services_stats' },
    { label: 'Service Rate', key: 'tier', mask: maskMoney }
  ];

  chefServiceListColumns = [
    { label: 'Date', key: 'date', sortable: false, mask: maskDate },
    {
      label: 'Time',
      sortable: false,
      combine: [
        { key: 'start_time', mask: maskServiceStartTime },
        { key: 'end_time', mask: maskServiceEndTime }
      ]
    },
    { label: 'Client', key: 'client', sortable: false, dive: 'name' },
    {
      label: 'Location',
      key: 'location'
    },
    {
      label: 'Service Type',
      key: 'service_type',
      sortable: false,
      mask: maskCapitalizeFirstLetter
    },
    {
      label: 'Rate',
      key: 'invited_chefs',
      mask: (invitedChefs) => {
        const currentChefInvite = (invitedChefs || []).find(invite => 
          _get(invite, 'chef.id') === _get(this, 'state.chef.id')
        );
        return currentChefInvite ? maskMoney(currentChefInvite.rate) : ""
      }
    },
    {
      link: { to: '/admin/service/[id]', label: 'View', target: '_blank' },
      sortable: false
    }
  ];

  render() {
    const {
      chef,
      unsaved,
      redirect_to,
      refresh,
      isLoading,
      timeOffIndex,
      zipMap
    } = this.state;
    const chefName = adminChefName(chef);
    //TODO should we handle verification pending differently?
    const isActive = chef.account_status === ACCOUNT_STATUSES.ACTIVE
    const isInactive = chef.account_status === ACCOUNT_STATUSES.INACTIVE
    const isDeactivated = chef.account_status === ACCOUNT_STATUSES.DEACTIVATED
    if (redirect_to) return <Redirect to={redirect_to} />;
    if (refresh) window.location.reload(false)

    if (isLoading) return <Loader />;

    return (
      <div className="cms">
        <div className="cms__head cms__head--padding">
          <div className="cms__content">
            <BackButton defaultUrl="/admin/chefs" />
            {chef.id && (
              <AccountStatus
                accountType="Chef"
                accountStatus={chef.account_status}
                date={isActive ? chef.created_at : chef.deactivation_date}
              />
            )}
            <div className="cms__bar">
              <HeadingPlaceholder
                value={chefName}
                placeholder="Chef"
                label=""
              />
                <div className="cms__bar-ctas">
                  <div
                    className="btn-icon btn-icon--trash"
                    onClick={() => this.openModal('delete')}
                  >
                    <SVGTrash />
                  </div>
                  <div
                    className="cms__bar-cta btn btn--link btn--full-mobile"
                    onClick={() => this.openModal('reset-password')}
                  >
                    Reset Password
                  </div>
                  <div
                    className="btn btn--hollow-blue"
                    onClick={() => this.openModal('edit-details')}
                  >
                    Edit Details
                  </div>
                  <div
                    className="btn btn--solid-blue"
                    onClick={this.handleSubmit}
                  >
                    Save
                  </div>
                  {!isActive &&
                    <div
                      className="btn btn--solid-blue"
                      onClick={() => this.changeAccountStatus(ACCOUNT_STATUSES.ACTIVE)}
                    >
                      Activate Account
                    </div>
                  }
                </div>
            </div>
            <ContactInformationBar
              phone={chef.phone_number}
              email={chef.email}
              unconfirmed_email={chef.unconfirmed_email}
            />
            <DetailsBar data={chef} columns={this.detailsBarProperties} />
          </div>
        </div>

        <div className="cms__body">
          <div className="cms__content">
            <div className="row">
              <ChefServices
                chef={chef}
                updateChef={this.updateChef}
                columns={this.chefServiceListColumns}
                zipMap={zipMap}
              />
            </div>
            <div className="row">
              <div className="row__item row__item--two-third row__item--pad-right">
                <ChefAvailabilitySection
                  chef={chef}
                  availabilities={chef.chef_availabilities}
                  timeOff={chef.chef_time_offs}
                  updateChef={this.updateChef}
                  revertAvailability={this.revertAvailability}
                  openModal={more => this.openModal('edit-time-off', more)}
                  isMobile={this.props.isMobile}
                />
              </div>
              <div className="row__item row__item--third row__item--pad-left">
                <ChefLocation
                  openModal={() => this.openModal('edit-locations')}
                  serviceDistricts={chef.service_district_subsections}
                  availableServiceDistricts={
                    this.state.availableServiceDistricts
                  }
                />
              </div>
            </div>

            <div className="cms__bar cms__section-head">
              <h3 className="h3">Private Notes</h3>
            </div>

            <div className="row">
              <div className="row__item row__item--half row__item--pad-right">
                <TextAreaView
                  value={_get(chef, 'notes.personality', '')}
                  openEdit={() => this.openModal('notes-personality')}
                  title="Personality / Specialty Notes"
                  subForm={true}
                />
              </div>
              <div className="row__item row__item--half row__item--pad-left" />
              <div className="row__item row__item--half row__item--pad-right">
                <TextAreaView
                  value={_get(chef, 'notes.bio', '')}
                  openEdit={() => this.openModal('notes-bio')}
                  title="Bio"
                  subForm={true}
                />
              </div>
              <div className="row__item row__item--half row__item--pad-left" />
              <div className="row__item row__item--half row__item--pad-right">
                <TextAreaView
                  value={_get(chef, 'notes.feedback', '')}
                  openEdit={() => this.openModal('notes-feedback')}
                  title="Feedback"
                  subForm={true}
                />
              </div>
              <div className="row__item row__item--half row__item--pad-left" />
            </div>
            <div className="row">
              {chef.id > 0 && !isDeactivated ? (
                  <div
                    className="btn btn--hollow-red"
                    onClick={() => this.openModal('toggle-status', { newStatus: ACCOUNT_STATUSES.DEACTIVATED})}
                  >
                    Deactivate
                  </div>
              ) : (
                ''
              )}
              {chef.id > 0 && !isInactive ? (
                  <div
                    className="btn btn--hollow-yellow btn--margin-left"
                    onClick={() => this.openModal('toggle-status', { newStatus: ACCOUNT_STATUSES.INACTIVE })}
                  >
                    Set as Inactive
                  </div>
              ) : (
                ''
              )}
            </div>
          </div>
        </div>

        <ChefEditDetailsModal
          chef={chef}
          updateChef={this.updateChef}
          isOpen={this.isOpen('edit-details')}
          isRoleAdmin={true}
          closeModal={this.closeModal}
          availableServiceDistricts={this.state.availableServiceDistricts}
          serviceDistricts={chef.service_district_subsections}
        />

        <ChefTimeOffModal
          isOpen={this.isOpen('edit-time-off')}
          closeModal={() => this.closeModal({ timeOffIndex: -1 })}
          index={timeOffIndex}
          chef_time_offs={chef.chef_time_offs}
          updateChef={this.updateChef}
        />

        <ChefLocationModal
          isOpen={this.isOpen('edit-locations')}
          closeModal={this.closeModal}
          availableServiceDistricts={this.state.availableServiceDistricts}
          serviceDistricts={chef.service_district_subsections}
          updateChef={this.updateChef}
        />

        <TextAreaModal
          title="Personality / Specialty Notes"
          value={_get(chef, 'notes.personality', '')}
          isOpen={this.isOpen('notes-personality')}
          closeModal={this.closeModal}
          onSubmit={notes => this.updateChef({ notes: { ...chef.notes, personality: notes}}) }
        />

        <TextAreaModal
          title="Bio"
          value={_get(chef, 'notes.bio', '')}
          isOpen={this.isOpen('notes-bio')}
          closeModal={this.closeModal}
          onSubmit={notes => this.updateChef({ notes: { ...chef.notes, bio: notes}}) }
        />

        <TextAreaModal
          title="Feedback"
          value={_get(chef, 'notes.feedback', '')}
          isOpen={this.isOpen('notes-feedback')}
          closeModal={this.closeModal}
          onSubmit={notes => this.updateChef({ notes: { ...chef.notes, feedback: notes}}) }
        />

        <ChefAccountStatusModal
          isOpen={this.isOpen('toggle-status')}
          closeModal={this.closeModal}
          newStatus={this.state.newStatus}
          chef={chef}
          changeAccountStatus={this.changeAccountStatus}
        />

        <ConfirmModal
          isOpen={this.isOpen('delete')}
          closeModal={this.closeModal}
          onSubmit={this.hardDeleteChef}
          heading="Delete This Chef?"
          body={`The chef "${chefName}" will permanently deleted.`}
          cta="Delete Chef"
        />

        <ConfirmModal
          isOpen={this.isOpen('reset-password')}
          closeModal={this.closeModal}
          onSubmit={this.handleResetPassword}
          heading="Reset Password"
          body="This will send an email to reset the password. Are you sure?"
          cta="Okay"
        />

        <Prompt when={unsaved} message="You have unsaved data, are you sure?" />
      </div>
    );
  }

  updateChef = updatedChef => {
    const chef = {
      ...this.state.chef,
      ...updatedChef
    };
    this.setState({ chef, unsaved: true, openModal: '', addressIndex: -1 });
  };

  revertAvailability = () => {
    const chef = { ...this.state.chef };
    chef.chef_availabilities = this.chefAvailabilitiesSnapshot;
    chef.chef_time_offs = this.chefTimeOffsSnapshot;
    this.setState({ chef });
  };

  loadChef(id) {
    this.setState({ isLoading: true });
    chefAPI
      .getChef(id)
      .then(chef => {
        this.setState({
          chef: {
            personal_addresses: [],
            chef_availabilities: [],
            ...chef
          },
          isLoading: false
        });
        // save a snapshot
        this.chefAvailabilitiesSnapshot = chef.chef_availabilities || [];
        this.chefTimeOffsSnapshot = chef.chef_time_offs || [];
      }) // todo: remove when BE provides 'personal_addresses'
      .catch(this.handleErrorLoading);
  }

  handleErrorLoading = error => {
    const pathname = '/admin/chefs';
    const redirect_to = { pathname };

    const { state } = this.props.location;
    if (state) {
      const { redirect_url, redirect_payload } = state;
      if (redirect_url) {
        redirect_to.pathname = redirect_url;
      }

      if (redirect_payload) redirect_to.state = { ...redirect_payload };
    }
    this.props.addMessage({
      className: 'error',
      message: stringfyServerErrors(error)
    }); // todo: add messaging via withSplashMessage
    this.setState({ redirect_to, isLoading: false });
  };

  hardDeleteChef = () => {
    const { chef } = this.state;
    chefAPI
      .deleteChef(chef.id, true)
      .then(success => {
        this.props.addMessage({
          className: 'success',
          message: `"${adminChefName(chef)}" has been deleted.`
        });
        this.setState({ redirect_to: '/admin/chefs', unsaved: false });
      })
      .catch(error => {
        this.props.addMessage({
          className: 'error',
          message: `Error deleting client: ${stringfyServerErrors(error)}`
        });
      });
  };

  handleResetPassword = () => {
    const { chef } = this.state;

    const LocalRedirectUrl = `${ENV_CONFIG.REACT_APP_SCHEMA}://${
      document.domain
    }/reset-password/chef`;

    const payload = {
      role: 'chef',
      email: chef.email,
      redirect_url: LocalRedirectUrl //`${ENV_CONFIG.REACT_APP_SCHEMA}://${ENV_CONFIG.REACT_APP_APP_HOST}/reset-password/chef`,
    };
    authAPI
      .forgotPassword(payload)
      .then(res => {
        this.props.addMessage({
          className: 'error',
          message:
            "We've just sent you an email with instructions on how to resend your password"
        });
        this.closeModal();
      })
      .catch(err => {
        this.props.addMessage({
          className: 'error',
          message: stringfyServerErrors(err)
        });
        this.closeModal();
      });
  };

  changeAccountStatus = (newStatus) => {
    const chef = { ...this.state.chef };

    switch(newStatus) {
      case ACCOUNT_STATUSES.DEACTIVATED:
        this.updateChefStatus(chefAPI.deleteChef, chef, 'deactivated')
        break;
      case ACCOUNT_STATUSES.INACTIVE:
        this.updateChefStatus(chefAPI.inactivateChef, chef, 'inactivated')
        break;
      case ACCOUNT_STATUSES.ACTIVE:
        this.updateChefStatus(chefAPI.restoreChef, chef, 'activated')
        break;
      default:
    }
  };

  updateChefStatus = (call, chef, action) => {
    call(chef.id)
      .then(_ => {
        this.props.addMessage({
          className: 'success',
          message: `"${adminChefName(chef)}" has been ${action}.`
        });
        this.setState({ chef, redirect_to: '/admin/chefs', unsaved: false });
      })
      .catch(error => {
        console.log(error);
        this.props.addMessage({
          className: 'error',
          message: `Chef ${action} error: ${error}`
        });
      });
  }

  handleSubmit = () => {
    const chef = { ...this.state.chef };
    if (chef.id)
      chefAPI
        .updateChef(chef)
        .then(chef => {
          this.props.addMessage({
            className: 'success',
            message: `"${adminChefName(chef)}" has been updated.`
          });
          this.setState({ chef, refresh: true, unsaved: false });
        })
        .catch(error => {
          console.log(error);
          this.props.addMessage({
            className: 'error',
            message: `Error saving chef: ${error}`
          });
        });
    else
      chefAPI
        .createChef(chef)
        .then(chef => {
          this.props.addMessage({
            className: 'success',
            message: `"${adminChefName(chef)}" has been created.`
          });
          this.setState({ chef, redirect_to: '/admin/chefs', unsaved: false });
        })
        .catch(error => {
          console.log(error);
          this.props.addMessage({
            className: 'error',
            message: `Error saving chef: ${error}`
          });
        });
  };

  maskAvailability(value) {
    return value ? 'Weekly & Occasion Services' : 'Weekly Services';
  }

  maskCompleted(value) {
    return ` (${value} completed)`;
  }

  generateNewChef() {
    this.setState({ chef: { ...this.defaultChefState } });
  }

  componentDidMount() {
    const { id } = this.props.match.params;
    if (id === 'create') this.generateNewChef();
    else if (id > 0) this.loadChef(id);

    chefAPI
      .getServiceDistricts()
      .then(({ service_districts: availableServiceDistricts }) => {
        this.setState({
          availableServiceDistricts,
          zipMap: createZipMap(availableServiceDistricts)
        });
      })
      .catch(err =>
        this.props.addMessage({
          className: 'error',
          message: stringfyServerErrors(err)
        })
      );
  }
}
export default withSplashMessage(AdminChef);



// WEBPACK FOOTER //
// ./src/components/admin/AdminChef.js